/**
 * ticket-inapp.scaled-card — the package's one scaling-frame mechanism, and
 * how EVERY on-screen surface mounts a card: lays `renderTicketVariantCard`
 * out at its real 384px design width inside a fixed-size frame, then shrinks
 * the WHOLE frame with a CSS `transform: scale()` down to the caller's
 * target width, so what ships is a scaled rendering of the real layout,
 * never a re-flow at the smaller size. A ticket is an identity document —
 * the card carries no breakpoint of its own (ticket-inapp.editorial.template.ts),
 * so this frame is what keeps a 320px phone's ticket the same object as a
 * desktop's, only smaller. Every caller — the buyer's order-access carousel,
 * an admin order-detail cell, the admin's own template preview
 * (`<ticket-template-preview>`, @momo/views) — hands over live
 * `TicketTemplateProps`; this package resolves none of its own, by its props
 * contract (ticket.props.ts). There is exactly one scale-frame implementation
 * in this package: this one.
 *
 * `pointer-events: none` rides the scaled frame only — the frame carries no
 * interactive element of its own, so this keeps a wrapping tile (the
 * picker's selectable surface) or a sibling overlay button (an order-detail
 * share action, positioned alongside — never inside — this function's
 * return) the sole click target; `pointer-events: none` on an element never
 * reaches a sibling, so a caller's own overlay button is unaffected either
 * way.
 *
 * `captureId` — WHY IT TAGS A SECOND, OFF-SCREEN (NEVER `visibility:
 * hidden`) NODE, MEASURED BY THIS SIGN: `domToCanvas`/`domToBlob` sizes its
 * OWN rasterize viewport from the tagged node's LIVE `getBoundingClientRect()`
 * — not `offsetWidth`, and not the `scale` option's arithmetic — so a node
 * that is merely transform-free ON ITSELF, while still nested inside the
 * scaled frame's transformed ancestor, still gets its `getBoundingClientRect()`
 * shrunk by that ancestor and is captured CROPPED to that shrunken viewport,
 * then enlarged: an incomplete image, not merely a wrongly-sized one (verified
 * against the real `modern-screenshot` build: a card tagged inside the frame
 * rasterizes as the top-left crop of the true design, text truncated
 * mid-word). The tagged node's rect must therefore be genuinely,
 * unconditionally 384 — reachable only by a node with NO transformed
 * ancestor at all, which means a second render, positioned off-screen
 * (`left: -9999px`) rather than inside the visible scaled frame.
 * `visibility: hidden` cannot stand in for "off-screen" here even though it
 * also preserves layout/rect: `modern-screenshot` paints nothing for a
 * `visibility: hidden` node (also verified against the real build — a
 * fully blank capture), because it clones computed style faithfully,
 * `visibility` included. `left: -9999px` keeps the node genuinely rendered
 * (a real, visible-per-CSS box the rasterizer can paint) while placing it
 * off the viewport a human never sees; `aria-hidden` and `pointer-events:
 * none` keep it out of the accessibility tree and the tab order. */
import type { TicketTemplateVariant } from "@momo/types";
import { html, nothing, type TemplateResult } from "lit";
import type { TicketTemplateProps } from "./ticket.props.ts";
import { renderTicketVariantCard } from "./ticket-inapp.card.ts";

/** The card's own design width — its `max-w-sm` (384px) ceiling, and the one
 *  width every card in this package is composed against, on screen and in
 *  the rasterized file alike (`TICKET_FILE_DESIGN_WIDTH`, ticket-file.page.ts). */
const FRAME_WIDTH = 384;
const FRAME_HEIGHT = FRAME_WIDTH * 1.5;

/** Renders `variant`'s card for `props` as a `width`-px-wide scaled
 *  rendering — the real card laid out at its native 384px width inside a
 *  `container-type: inline-size` frame, then scaled down whole via CSS
 *  `transform`, never re-flowed. `width === FRAME_WIDTH` (384) is a true
 *  no-op: `scale` resolves to 1, an identity transform. Pass `captureId` to
 *  additionally mount a second, off-screen, native-384px render tagged
 *  `data-ticket-canvas` — see this file's own header for why the capture
 *  target must be this separate node, never the visible scaled one. */
export function renderTicketCardScaled(
	variant: TicketTemplateVariant,
	props: TicketTemplateProps,
	{ width, captureId }: { width: number; captureId?: string },
): TemplateResult {
	const scale = width / FRAME_WIDTH;
	const height = FRAME_HEIGHT * scale;
	return html`
		<div class="relative overflow-hidden rounded-2xl" style="width:${width}px;height:${height}px;">
			<div
				class="pointer-events-none overflow-hidden"
				style="width:${FRAME_WIDTH}px;height:${FRAME_HEIGHT}px;transform:scale(${scale});transform-origin:top left;"
			>
				${renderTicketVariantCard(variant, props)}
			</div>
			${
				captureId
					? html`
					<div
						data-ticket-canvas=${captureId}
						aria-hidden="true"
						class="pointer-events-none absolute top-0"
						style="left:-9999px;width:${FRAME_WIDTH}px;"
					>
						${renderTicketVariantCard(variant, props)}
					</div>
				`
					: nothing
			}
		</div>
	`;
}
